/**
 * ServiceWorkflowRouter.js - Routes jobs to the correct service-specific workflow
 * 
 * CRITICAL: The JOB TYPE (service_type) determines which workflow opens, NOT the user's role.
 * A worker can have multiple capabilities (cleaning, laundry, courier, rides)
 * and each job opens its specific workflow.
 */
import React, { useState, useCallback } from 'react';

// Import service-specific workflow modals
import LaundryWorkflowModal from './LaundryWorkflowModal';
import CleaningWorkflowModal from './CleaningWorkflowModal';
import RideWorkflowModal from './RideWorkflowModal';
import CourierWorkflowModal from './CourierWorkflowModal';
import JobWorkflowModal from './JobWorkflowModal';  // Fallback/generic workflow (errands, tasks)

// Service type detection helper
const detectServiceType = (job) => {
  if (!job) return 'generic';
  
  const serviceType = (job.service_type || '').toLowerCase();
  const serviceName = (job.service_name || '').toLowerCase();
  const jobType = (job.job_type || job.type || '').toLowerCase();
  
  // PRIORITY 1: Explicit service_type check first (most reliable)
  if (serviceType === 'courier' || serviceType === 'delivery') return 'courier';
  if (serviceType === 'laundry') return 'laundry';
  if (serviceType === 'cleaning') return 'cleaning';
  if (serviceType === 'rides' || serviceType === 'ride' || serviceType === 'driver') return 'rides';
  if (serviceType === 'errands' || serviceType === 'tasks') return 'errands';
  
  // PRIORITY 2: Check for courier/delivery jobs (before rides to avoid misclassification)
  if (
    jobType === 'courier' ||
    serviceName.includes('courier') ||
    serviceName.includes('delivery') ||
    serviceName.includes('package') ||
    job.package_info ||
    job.courier_info
  ) {
    return 'courier';
  }
  
  // PRIORITY 3: Check for ride/driver jobs
  if (
    jobType === 'ride' ||
    serviceName.includes('ride') ||
    job.is_ride === true ||
    // Has pickup/dropoff but NOT a courier (checked earlier)
    (job.pickup_address && job.dropoff_address && !job.package_info && !job.courier_info)
  ) {
    return 'rides';
  }
  
  // Check for laundry jobs
  if (
    jobType === 'laundry' ||
    serviceName.includes('laundry') ||
    serviceName.includes('wash') ||
    serviceName.includes('dry clean') ||
    job.laundry_info
  ) {
    return 'laundry';
  }
  
  // Check for cleaning jobs
  if (
    jobType === 'cleaning' ||
    serviceName.includes('clean') ||
    serviceName.includes('maid') ||
    serviceName.includes('housekeep') ||
    job.cleaning_details
  ) {
    return 'cleaning';
  }
  
  // Check for errands/tasks jobs
  if (
    jobType === 'errand' ||
    jobType === 'task' ||
    serviceName.includes('errand') ||
    serviceName.includes('task')
  ) {
    return 'errands';
  }
  
  // Fallback to generic workflow
  return 'generic';
};

// Get workflow label for display
const getWorkflowLabel = (serviceType) => {
  switch (serviceType) {
    case 'rides': return 'Ride';
    case 'courier': return 'Courier';
    case 'laundry': return 'Laundry';
    case 'errands': return 'Errands';
    default: return 'Job';
  }
};

// Get workflow color for display
const getWorkflowColor = (serviceType) => {
  switch (serviceType) {
    case 'rides': return 'purple';
    case 'courier': return 'orange';
    case 'laundry': return 'cyan';
    case 'cleaning': return 'blue';
    case 'errands': return 'green';
    default: return 'gray';
  }
};

/**
 * ServiceWorkflowRouter Component
 * 
 * Props:
 * - isOpen: boolean - Whether the workflow modal is open
 * - onClose: function - Called when workflow closes
 * - job: object - The job/booking object with service_type
 * - worker: object - The current worker's profile
 * - onWorkflowComplete: function - Called when workflow is completed
 */
const ServiceWorkflowRouter = ({ 
  isOpen, 
  onClose, 
  job, 
  worker,
  onWorkflowComplete 
}) => {
  // Detect the service type from the job
  const serviceType = detectServiceType(job);
  
  // Handle workflow completion
  const handleComplete = useCallback((completionData) => {
    console.log(`[ServiceWorkflowRouter] ${serviceType} workflow completed:`, completionData);
    onWorkflowComplete?.(completionData);
  }, [serviceType, onWorkflowComplete]);

  // Don't render if not open or no job
  if (!isOpen || !job) return null;

  // Route to the correct workflow based on service type
  switch (serviceType) {
    case 'rides':
      return (
        <RideWorkflowModal
          isOpen={isOpen}
          onClose={onClose}
          ride={job}
          driver={worker}
          onWorkflowComplete={handleComplete}
        />
      );
      
    case 'laundry':
      return (
        <LaundryWorkflowModal
          isOpen={isOpen}
          onClose={onClose}
          booking={job}
          worker={worker}
          onWorkflowComplete={handleComplete}
        />
      );
      
    case 'cleaning':
      return (
        <CleaningWorkflowModal
          isOpen={isOpen}
          onClose={onClose}
          booking={job}
          worker={worker}
          onWorkflowComplete={handleComplete}
        />
      );
      
    case 'courier':
      return (
        <CourierWorkflowModal
          isOpen={isOpen}
          onClose={onClose}
          booking={job}
          worker={worker}
          onWorkflowComplete={handleComplete}
        />
      );
      
    case 'errands':
    default:
      // Use the generic JobWorkflowModal for errands and unknown types
      return (
        <JobWorkflowModal
          isOpen={isOpen}
          onClose={onClose}
          booking={job}
          worker={worker}
          onWorkflowComplete={handleComplete}
        />
      );
  }
};

// Export utilities for external use
export { 
  ServiceWorkflowRouter as default,
  detectServiceType,
  getWorkflowLabel,
  getWorkflowColor
};
